import React, { useEffect, useRef, useState } from 'react';
import useResizeObserver from '@react-hook/resize-observer';
import cx from 'clsx';
import debounce from 'lodash.debounce';

import { ReactComponent as QueryIcon } from '../../../../../assets/icons/answers/search-query-icon.svg';
import { Tooltip } from '../../../../Tooltips/Tooltip';

import styles from './SearchQuery.module.scss';

const ELEMENTS_GAP = parseInt(styles.itemsGap, 10);
const SET_WIDTH_DELAY = 30;

type QueryWithWidth = {
    label: string;
    width: number;
};

export type SearchQueryProps = {
    queries?: string[];
    className?: string;
};

const calculateQueryWidth = (queries: string[]): QueryWithWidth[] => {
    const chipsToRender: QueryWithWidth[] = [];

    if (queries.length === 0) {
        return chipsToRender;
    }

    // Create a hidden container to measure button sizes
    const wrapper = document.createElement('div');
    wrapper.className = styles.searchQueries;
    wrapper.style.position = 'absolute';
    wrapper.style.visibility = 'hidden';
    wrapper.style.pointerEvents = 'none';
    wrapper.style.top = '-10000px';
    wrapper.style.left = '-10000px';
    document.body.appendChild(wrapper);

    try {
        for (const query of queries) {
            const label = query.trim();

            if (!label) {
                continue;
            }

            const element = document.createElement('div');
            element.className = styles.query;
            element.innerText = label;
            element.style.visibility = 'hidden';

            wrapper.appendChild(element);

            chipsToRender.push({ label: `"${label}"`, width: element.offsetWidth });
        }
    } finally {
        // clean up the hidden container
        if (wrapper.parentNode) {
            wrapper.parentNode.removeChild(wrapper);
        }
    }

    return chipsToRender;
};

export const SearchQueryComponent: React.FC<SearchQueryProps> = ({ queries, className }) => {
    const visibleQueries = useRef<QueryWithWidth[]>(calculateQueryWidth(queries ?? [])).current;
    const [wrapper, setWrapper] = useState<HTMLDivElement | null>(null);
    const [availableWidth, setAvailableWidth] = useState<number>(0);

    const setWidthDebounced = useRef(
        debounce((wrapper: HTMLDivElement | null) => {
            setAvailableWidth(wrapper?.offsetWidth ?? 0);
        }, SET_WIDTH_DELAY)
    ).current;

    useResizeObserver(wrapper, (entry) => {
        setWidthDebounced(entry.target as HTMLDivElement);
    });

    useEffect(() => {
        return () => {
            if (typeof setWidthDebounced.cancel === 'function') {
                setWidthDebounced.cancel();
            }
        };
    }, [wrapper, setWidthDebounced]);

    const queriesToRender = React.useMemo(() => {
        const toRender: string[] = [];
        let requiredSpace = 0;
        for (let i = 0; i < visibleQueries.length; i++) {
            const query = visibleQueries[i];
            const isLastItem = i === visibleQueries.length - 1;

            toRender.push(query.label);

            requiredSpace += query.width;

            if (toRender.length > 1 && !isLastItem) {
                requiredSpace += ELEMENTS_GAP;
            }

            if (!isLastItem && requiredSpace > availableWidth) {
                break;
            }
        }

        return toRender;
    }, [visibleQueries, availableWidth]);

    if (!queries?.length) {
        return null;
    }

    return (
        <Tooltip
            isDisabled={queriesToRender.length === queries.length}
            label={queries.join(', ')}
            hasArrow
            placement="top"
        >
            <div
                ref={(node) => {
                    setAvailableWidth(node?.offsetWidth ?? 0);
                    setWrapper(node);
                }}
                className={cx(className, styles.searchQueries)}
            >
                {queriesToRender.map((query, idx, array) => (
                    <div key={query} className={cx(styles.query, idx === array.length - 1 && styles.last)}>
                        <QueryIcon />
                        {query}
                    </div>
                ))}
            </div>
        </Tooltip>
    );
};

export const SearchQuery = React.memo(SearchQueryComponent);
